import { useContext, useCallback, useState, useEffect, useMemo } from 'react';
import { QuizContext } from 'src/views/Quiz/Quiz.context';
import { QUIZ_EVENT_TYPES } from 'src/types/Quiz';
import { Input, useDebounce } from '@get-mosh/msh-shared';
import { useAddressValidHDS } from 'src/hooks/useAddressValidHDS';
import { Vertical } from 'src/constants/Verticals';
import { usePostcodeSearch } from 'src/hooks/usePostcodeSearch';

const INVALID_HDS_WARNING_TEXT =
    'Our delivery partner may be unable to deliver refrigerated items to your suburb';

const QuizSuburb = () => {
    const { quizToken, quizType, currentQuestion, handleSubmitAnswer, state } =
        useContext(QuizContext);
    const vertical = useMemo(
        () => Vertical[quizType.toUpperCase()],
        [quizType],
    );
    const {
        answerList,
        questionImage,
        questionText,
        questionExplanation,
        userAnswers,
        additionalText,
    } = currentQuestion || {};
    const [continueDisabled, setContinueDisabled] = state.continueDisabled;
    const [suburb, setSuburb] = useState('');
    const [postcode, setPostcode] = useState('');
    const [stateName, setStateName] = useState('');
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 500);
    const { data: addresses, isLoading } = usePostcodeSearch({
        query: debouncedSearch,
    });
    const { data, isLoading: processingValidHDS } = useAddressValidHDS({
        suburb,
        postcode,
        quizToken: quizToken || '',
        disabled: vertical !== Vertical.WEIGHT_LOSS,
    });
    const { is_serviceable: isValidHDS } = data || {};
    const addressValid = suburb && postcode && stateName;

    // Handle submitting
    const handleContinue = useCallback(() => {
        if (currentQuestion.questionId && !continueDisabled) {
            const { answerId: suburbAnswerId = '' } = answerList[0] || {};
            const { answerId: postcodeAnswerId = '' } = answerList[1] || {};
            const { answerId: stateAnswerId = '' } = answerList[2] || {};
            handleSubmitAnswer({
                questionId: currentQuestion.questionId,
                questionText: currentQuestion.questionText,
                answerIds: [suburbAnswerId, postcodeAnswerId, stateAnswerId],
                answerTexts: [suburb, postcode, stateName],
            });
        }
    }, [
        postcode,
        suburb,
        stateName,
        answerList,
        continueDisabled,
        currentQuestion,
        handleSubmitAnswer,
    ]);

    // Enter key press listener
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && addressValid) handleContinue();
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () => {
            window.removeEventListener('keydown', handleKeyPress, false);
        };
    }, [handleContinue, addressValid]);

    // Continue click listener
    useEffect(() => {
        window.addEventListener(QUIZ_EVENT_TYPES.continueClick, handleContinue);

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.continueClick,
                handleContinue,
            );
    }, [handleContinue]);

    useEffect(() => {
        if (
            addressValid &&
            (vertical !== Vertical.WEIGHT_LOSS ||
                (vertical === Vertical.WEIGHT_LOSS &&
                    !processingValidHDS &&
                    typeof isValidHDS === 'boolean'))
        ) {
            setContinueDisabled(false);
        } else {
            setContinueDisabled(true);
        }
    }, [
        addressValid,
        processingValidHDS,
        isValidHDS,
        vertical,
        setContinueDisabled,
    ]);

    // Set existing answer
    useEffect(() => {
        let newSuburb: string | null = null;
        let newPostcode: string | null = null;
        let newState: string | null = null;
        if (userAnswers.length > 0) {
            newSuburb = userAnswers[0].answerText;
            newPostcode = userAnswers[1].answerText;
            newState = userAnswers[2].answerText;
        }
        if (newSuburb && newPostcode) {
            setSuburb(newSuburb);
            setPostcode(newPostcode);
            setStateName(newState || '');
        }
    }, [currentQuestion.questionId, userAnswers, setContinueDisabled]);

    return (
        <div className="flex w-full flex-col gap-6 pb-44">
            <picture>
                <img
                    src={questionImage}
                    className="h-16 w-16"
                    alt={questionText}
                />
            </picture>
            <div>
                <h4>{questionText}</h4>
                {questionExplanation ? (
                    <h5 className="highlight text-grey-400">
                        {questionExplanation}
                    </h5>
                ) : null}
            </div>
            <div className="relative">
                <Input
                    data-testid="auspost-suburb-input"
                    id="autocomplete"
                    name="suburb"
                    placeholder={additionalText}
                    onFocus={() => {
                        if (suburb && postcode) {
                            setSearch(`${suburb}, ${postcode}`);
                            setSuburb('');
                            setPostcode('');
                            setStateName('');
                        }
                    }}
                    value={
                        search ||
                        (suburb && postcode ? `${suburb}, ${postcode}` : '')
                    }
                    onChange={(e) => {
                        setSearch(e.target.value);
                    }}
                    error={
                        addresses && addresses.length === 0
                            ? 'No results found'
                            : ''
                    }
                    isLoading={isLoading}
                />
                {addresses && addresses.length > 0 ? (
                    <div className="border-grey-200 absolute -mt-4 max-h-44 w-full overflow-y-auto rounded-lg border bg-white">
                        {addresses.map((address) => (
                            <div
                                key={`${address.suburb}${address.postcode}`}
                                className="hover:bg-grey-100 hover:font-demi flex flex-row gap-1 rounded-sm p-4"
                                onClick={() => {
                                    setSuburb(address.suburb);
                                    setPostcode(address.postcode);
                                    setStateName(address.state);
                                    setSearch('');
                                }}
                            >
                                <span className="text-grey-400">
                                    {address.suburb}
                                </span>
                                <span className="font-demi text-grey-400">
                                    {address.postcode}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
            {isValidHDS === false ? (
                <div className="p3 bg-yellow-warning text-orange-dark -mt-[44px] rounded-lg p-2">
                    {INVALID_HDS_WARNING_TEXT}
                </div>
            ) : null}
        </div>
    );
};

export default QuizSuburb;
